// docs/141: the fade through black between screens. `useRoute.go` asks `curtainFor` whether a move
// from one screen to another goes through the black; if it does, the go waits while the screen fades
// down (and, leaving the overworld for a place, the chart zooms into the band), the screens swap under
// the black, the black holds a moment while the new screen draws, and then it lifts. The quest path's
// own walk into a stop keeps its curtain (`pathCurtain.ts`, docs/113 QP7); this one shares its look.
import { create } from 'zustand';
import type { Screen } from './route';
import { deviceNow } from '../time';
import { CURTAIN_CLOSE_MS, CURTAIN_HOLD_MS, CURTAIN_OPEN_MS } from './pathCurtain';

/** 'zoom': the overworld zooms into where the band stands as it fades (docs/113's walk into a stop). */
export type CurtainKind = 'fade' | 'zoom';
export type ScreenCurtainPhase = 'idle' | 'closing' | 'black' | 'opening';

/** A plain fade down (ms); the zoom's fade is the path's own (`CURTAIN_CLOSE_MS`). */
export const FADE_CLOSE_MS = 600;
export const FADE_HOLD_MS = CURTAIN_HOLD_MS;
export const FADE_OPEN_MS = CURTAIN_OPEN_MS;
export const closeMs = (kind: CurtainKind) => (kind === 'zoom' ? CURTAIN_CLOSE_MS : FADE_CLOSE_MS);

const STORY: readonly Screen[] = ['writing', 'intro', 'epilogue', 'chapter'];
const BOARD: readonly Screen[] = ['encounter', 'spoils'];

/** docs/141 SF1: which moves between screens go through the black, and how. */
export function curtainFor(from: Screen, to: Screen): CurtainKind | undefined {
  if (from === to) return undefined;
  // into a place from the overworld: a village, a quest's map
  if (from === 'world' && (to === 'village' || to === 'localmap')) return 'zoom';
  // out of a village onto the overworld
  if (from === 'village' && to === 'world') return 'fade';
  // the lobby into the World's story (by the World screen and the writing screen), and between the story's screens
  if (from === 'gathering' && (to === 'world' || STORY.includes(to))) return 'fade';
  if (from === 'world' && STORY.includes(to)) return 'fade';
  if (STORY.includes(from) && (STORY.includes(to) || to === 'world')) return 'fade';
  // the intro's last scene into the first battle
  if (from === 'intro' && to === 'encounter') return 'fade';
  // out of an encounter (its board or its Spoils), wherever the route goes next
  if (BOARD.includes(from) && !BOARD.includes(to)) return 'fade';
  return undefined;
}

interface Pending { to: Screen; swap: () => void }

interface ScreenCurtainState {
  phase: ScreenCurtainPhase;
  kind: CurtainKind;
  phaseAt: number;
  /** The screen the black is taking the player to (while it closes). */
  to?: Screen;
  /** The overworld's zoom into the band (set by the World screen while it is up). */
  zoom?: () => void;
}

export const useScreenCurtain = create<ScreenCurtainState>(() => ({ phase: 'idle', kind: 'fade', phaseAt: 0 }));

let pending: Pending | undefined;
let timer: ReturnType<typeof setTimeout> | undefined;
const later = (ms: number, f: () => void) => { if (timer) clearTimeout(timer); timer = setTimeout(() => { timer = undefined; f(); }, ms); };
const phase = (p: ScreenCurtainPhase, extra: Partial<ScreenCurtainState> = {}) => useScreenCurtain.setState({ phase: p, phaseAt: deviceNow(), ...extra });

/** Hold the black a moment over the screen just swapped in, then lift it. */
function holdThenOpen() {
  phase('black', { to: undefined });
  later(FADE_HOLD_MS, () => { phase('opening'); later(FADE_OPEN_MS, () => phase('idle')); });
}

/**
 * Go to `to` through the black (`swap` makes the move). A second ask while the black closes on the
 * same screen takes its newer `swap`; on another screen it is taken there instead. An ask while the
 * black is already down (the World screen handing straight on to the writing screen) swaps at once
 * and holds the black again.
 */
export function cover(kind: CurtainKind, to: Screen, swap: () => void): void {
  const s = useScreenCurtain.getState();
  if (s.phase === 'black') { swap(); holdThenOpen(); return; }
  if (s.phase === 'closing') { pending = { to, swap }; useScreenCurtain.setState({ to }); return; }
  pending = { to, swap };
  phase('closing', { kind, to });
  if (kind === 'zoom') s.zoom?.();
  later(closeMs(kind), () => {
    const p = pending;
    pending = undefined;
    p?.swap();
    holdThenOpen();
  });
}

/** A plain move to another screen while the black closes: it wins, and the black lifts over it. */
export function uncover(): void {
  if (useScreenCurtain.getState().phase !== 'closing') return;
  pending = undefined;
  holdThenOpen();
}

/** Whether a move is waiting under the black (`to` the screen it waits to show). */
export const curtainPending = (): Screen | undefined => pending?.to;
